import React, { useMemo, Fragment, useEffect, useState } from "react";
import {
  MaterialReactTable,
  MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
  MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton,
} from "material-react-table";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplayIcon from "@mui/icons-material/Replay";
import Tooltip from "@mui/material/Tooltip";
import {
  useNavigate,
  Link,
  useOutletContext,
  useLocation,
} from "react-router-dom";
import Stack from "@mui/material/Stack";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import MpgInfoDialog from "../../../../common/dialog/MpgInfoDialog";
import MpgRedefinedConfirmDialog from "../../../../common/dialog/MpgRedefinedConfirmDialog";
import NoTableDataComp from "../../../../common/otherCommonComponents/NoTableDataComp";
import HtmlTooltip from "../../../../common/tooltips/HtmlTooltip";
import { useGetLoggedInUserDetails } from "../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useDeleteProgramDescriptorMutation,
  useViewProgrammeDescriptorListMutation,
  useGetAllClientForPDMutation,
} from "../../../../reactQueries/AppApi/rqHooksForProgrammeDescriptor";
import useTrackUserActivity from "../../../../userActivityTracking/useTrackUserActivity";
import { getAccessTypes } from "../../../../hooksAndFunctions/functions/getAccessTypes";
import ExportButton from "../../../../common/otherCommonComponents/ExportButton";
import { useTrackActivityMutation } from "../../../../reactQueries/commonRQHooks";
import {
  managePDCreatePath,
  managePDEditPath,
  managePDRecyclePath,
} from "../../../../routes/routePaths";

const returnLabel = (labelArray, key) => {
  if (labelArray?.length > 0 && key) {
    return labelArray.find((x) => x.Key === key)?.Phrase || "";
  }
  return "";
};

const TooltipCell = ({ renderedCellValue }) => (
  <HtmlTooltip title={renderedCellValue || ""}>
    {renderedCellValue || ""}
  </HtmlTooltip>
);

const NotSpecifiedTooltipCell = ({ renderedCellValue }) => (
  <HtmlTooltip title={renderedCellValue || "Not specified"}>
    {renderedCellValue || ""}
  </HtmlTooltip>
);

const NotSpecifiedSpanTooltipCell = ({ renderedCellValue }) => (
  <HtmlTooltip title={renderedCellValue || "Not specified"}>
    <span>{renderedCellValue || ""}</span>
  </HtmlTooltip>
);

const EmptyCell = ({ renderedCellValue }) => renderedCellValue || "";

const formatDate = (timestamp, includeTime = false) => {
  if (!timestamp) return "";
  try {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    const dateOptions = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };

    if (!includeTime) {
      return date.toLocaleDateString("en-GB", dateOptions);
    }

    return date.toLocaleString("en-GB", {
      ...dateOptions,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
};

const getLanguage = (row) => row?.Language || "";
const getResourceOwner = (row) => row?.ResourceOwner || "";
const getResourceUpdated = (row) => formatDate(row?.ResourceUpdated);
const getResourceStatus = (row) => (row.IsActive ? "Active" : "Inactive");
const getLastUpdatedBy = (row) => row.UpdatedBy || row.CreatedBy || "";

const getLastUpdatedOn = (row) => {
  if (row.UpdatedTimeStamp) {
    return formatDate(row.UpdatedTimeStamp, true);
  }
  if (row.CreatedTimeStamp) {
    return formatDate(row.CreatedTimeStamp, true);
  }
  return "";
};

const ViewClientAccPD = () => {
  const { loggedInUser } = useGetLoggedInUserDetails();
  const {
    clientPortalAdminRoleId,
    isEnterpriseAdmin,
    id: userId,
  } = loggedInUser;
  const { mutate: trackActivityMutate } = useTrackActivityMutation();

  const location = useLocation();
  const isRecycleRoute = location.pathname === managePDRecyclePath;

  const { clientPortalAdminType } = getAccessTypes();
  const { hasA4Access, hasA1Access } = clientPortalAdminType;
  const canViewNewFields = isEnterpriseAdmin || hasA1Access;
  const canExportXls = isEnterpriseAdmin || hasA1Access;

  const { mutateAsync: checkAssociationsAsync } = useGetAllClientForPDMutation();

  const [associationError, setAssociationError] = useState("");
  const [openAssociationDialog, setOpenAssociationDialog] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);

  const {
    mutate: getPDList,
    isSuccess,
    data: PDData,
    isError,
    error,
    reset: resetPDList,
    isLoading: isLoadingOnPDData,
  } = useViewProgrammeDescriptorListMutation();

  const {
    mutate: deletePDById,
    isSuccess: isSuccessOnDelete,
    variables: variablesOnDelete,
  } = useDeleteProgramDescriptorMutation();

  useEffect(() => {
    getPDList({
      loggedInUserId: userId,
      roleId: clientPortalAdminRoleId,
      isEnterpriseAdmin,
    });
  }, [loggedInUser]);

  useEffect(() => {
    if (isSuccessOnDelete) {
      getPDList({
        loggedInUserId: userId,
        roleId: clientPortalAdminRoleId,
        isEnterpriseAdmin,
      });
    }
  }, [isSuccessOnDelete]);

  const resourcesCMSData = useOutletContext();

  const fetchClientAccResListLabel = (key) => {
    if (resourcesCMSData?.pageContent?.["ClientAccountResourcesList"]) {
      return returnLabel(
        resourcesCMSData.pageContent["ClientAccountResourcesList"].Labels,
        key
      );
    }
    return "";
  };

  const fetchClientAccResDtlsLabel = (key) => {
    if (resourcesCMSData?.pageContent?.["ClientAccountResourcesDetails"]) {
      return returnLabel(
        resourcesCMSData.pageContent["ClientAccountResourcesDetails"].Labels,
        key
      );
    }
    return "";
  };

  const fetchMngResoRepoListLabel = (key) => {
    if (resourcesCMSData?.pageContent?.["ManageResourceRepoList"]) {
      return returnLabel(
        resourcesCMSData.pageContent["ManageResourceRepoList"].Labels,
        key
      );
    }
    return "";
  };

  const fetchCommonCMSLabel = (key) => {
    if (resourcesCMSData?.pageContent?.["CommonComponent"]) {
      return returnLabel(
        resourcesCMSData.pageContent["CommonComponent"].Labels,
        key
      );
    }
    return "";
  };

  const filteredPDListForStandardType =
    PDData?.data?.filter(
      (eachItem) => eachItem?.ProgramDescripitorType === "Standard"
    ) || [];

  const repoData =
    filteredPDListForStandardType.filter((item) => !!item.IsActive) || [];
  const recycleData =
    filteredPDListForStandardType.filter((item) => !item.IsActive) || [];
  const tableData = isRecycleRoute ? recycleData : repoData;

  const resourceRepositoryTitle =
    resourcesCMSData?.pageContent?.["ManageResourceRepoList"]?.Title ||
    fetchMngResoRepoListLabel("ManageResourceRepoList.ResourceRepoTitle");
  const exportFileName = `${resourceRepositoryTitle}.xlsx`;
  const exportWorksheetName = resourceRepositoryTitle;

  const exportColumns = [
    {
      id: "programDescripitorTitle",
      header: fetchClientAccResDtlsLabel(
        "ClientAccountResourcesDetails.ResourceTitle"
      ),
      getValue: (row) => row?.ProgramDescripitorTitle || "",
    },
    {
      id: "ProgramDescripitorFileName",
      header: fetchClientAccResDtlsLabel(
        "ClientAccountResourcesDetails.ResourceFileName"
      ),
      getValue: (row) => row?.ProgramDescripitorFileName || "",
    },
    ...(canViewNewFields
      ? [
          {
            id: "language",
            header:
              fetchClientAccResDtlsLabel(
                "ClientAccountResourcesDetails.ResourceLanguage"
              ) || "Language",
            getValue: (row) => row?.Language || "",
          },
          {
            id: "resourceOwner",
            header:
              fetchClientAccResDtlsLabel(
                "ClientAccountResourcesDetails.ResourceOwner"
              ) || "Owner",
            getValue: (row) => row?.ResourceOwner || "",
          },
          {
            id: "countriesDisplay",
            header:
              fetchClientAccResDtlsLabel(
                "ClientAccountResourcesDetails.ResourceCountry"
              ) || "Countries",
            getValue: (row) => row?.CountriesDisplay || "",
          },
          {
            id: "durationsDisplay",
            header:
              fetchClientAccResDtlsLabel(
                "ClientAccountResourcesDetails.ResourceDuration"
              ) || "Duration",
            getValue: (row) => row?.DurationsDisplay || "",
          },
          {
            id: "resourceUpdated",
            header:
              fetchClientAccResDtlsLabel(
                "ClientAccountResourcesDetails.ResourceUpdated"
              ) || "Resource Updated",
            getValue: (row) =>
              row?.ResourceUpdated ? formatDate(row.ResourceUpdated) : "",
          },
        ]
      : []),
    {
      id: "isActive",
      header: fetchClientAccResDtlsLabel(
        "ClientAccountResourcesDetails.Status"
      ),
      getValue: (row) => (row?.IsActive ? "Active" : "Inactive"),
    },
    {
      id: "lastUpdatedBy",
      header: fetchClientAccResListLabel(
        "ClientAccountResourcesList.LastUpdatedBy"
      ),
      getValue: (row) => row?.UpdatedBy || row?.CreatedBy || "",
    },
    {
      id: "lastUpdatedOn",
      header: fetchClientAccResListLabel(
        "ClientAccountResourcesList.LastUpdatedOn"
      ),
      getValue: (row) => {
        const timestamp = row?.UpdatedTimeStamp || row?.CreatedTimeStamp;
        return timestamp ? formatDate(timestamp, true) : "";
      },
    },
  ];

  const columns = useMemo(
    () => [
      {
        accessorKey: "ProgramDescripitorTitle",
        header: fetchClientAccResDtlsLabel(
          "ClientAccountResourcesDetails.ResourceTitle"
        ),
        id: "programDescripitorTitle",
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccResDtlsLabel(
            "ClientAccountResourcesDetails.PlaceholderResourceTitle"
          ),
        },
      },
      {
        accessorKey: "ProgramDescripitorFileName",
        header: fetchClientAccResDtlsLabel(
          "ClientAccountResourcesDetails.ResourceFileName"
        ),
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccResDtlsLabel(
            "ClientAccountResourcesDetails.PlaceholderResourceFileName"
          ),
        },
      },

      ...(canViewNewFields
        ? [
            {
              accessorFn: getLanguage,
              id: "language",
              header:
                fetchClientAccResDtlsLabel(
                  "ClientAccountResourcesDetails.ResourceLanguage"
                ) || "Language",
              Cell: TooltipCell,
              filterFn: "contains",
              muiTableHeadCellFilterTextFieldProps: {
                placeholder:
                  fetchClientAccResDtlsLabel(
                    "ClientAccountResourcesDetails.PlaceholderResourceLanguage"
                  ) || "Filter by language...",
              },
            },
            {
              accessorFn: getResourceOwner,
              id: "resourceOwner",
              header:
                fetchClientAccResDtlsLabel(
                  "ClientAccountResourcesDetails.ResourceOwner"
                ) || "Owner",
              Cell: TooltipCell,
              filterFn: "contains",
              muiTableHeadCellFilterTextFieldProps: {
                placeholder:
                  fetchClientAccResDtlsLabel(
                    "ClientAccountResourcesDetails.PlaceholderResourceOwner"
                  ) || "Filter by owner...",
              },
            },

            {
              accessorKey: "CountriesDisplay",
              header:
                fetchClientAccResDtlsLabel(
                  "ClientAccountResourcesDetails.ResourceCountry"
                ) || "Countries",
              id: "countriesDisplay",
              Cell: NotSpecifiedTooltipCell,
              filterFn: "contains",
              muiTableHeadCellFilterTextFieldProps: {
                placeholder:
                  fetchClientAccResDtlsLabel(
                    "ClientAccountResourcesDetails.PlaceholderResourceCountry"
                  ) || "Filter by countries...",
              },
            },
            {
              accessorKey: "DurationsDisplay",
              header:
                fetchClientAccResDtlsLabel(
                  "ClientAccountResourcesDetails.ResourceDuration"
                ) || "Duration",
              id: "durationsDisplay",
              Cell: NotSpecifiedTooltipCell,
              filterFn: "contains",
              muiTableHeadCellFilterTextFieldProps: {
                placeholder:
                  fetchClientAccResDtlsLabel(
                    "ClientAccountResourcesDetails.PlaceholderResourceDuration"
                  ) || "Filter by duration...",
              },
            },
            {
              accessorFn: getResourceUpdated,
              id: "resourceUpdated",
              header:
                fetchClientAccResDtlsLabel(
                  "ClientAccountResourcesDetails.ResourceUpdated"
                ) || "Resource Updated",
              size: 180,
              Cell: NotSpecifiedSpanTooltipCell,
              filterFn: "contains",
              sortingFn: "alphanumeric",
              muiTableHeadCellFilterTextFieldProps: {
                placeholder:
                  fetchClientAccResDtlsLabel(
                    "ClientAccountResourcesDetails.PlaceholderResourceUpdated"
                  ) || "Filter by resource updated...",
              },
            },
          ]
        : []),

      {
        accessorFn: getResourceStatus,
        id: "isActive",
        header: fetchClientAccResDtlsLabel(
          "ClientAccountResourcesDetails.Status"
        ),
        Cell: EmptyCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccResDtlsLabel(
            "ClientAccountResourcesDetails.PlaceholderStatus"
          ),
        },
      },
      {
        accessorFn: getLastUpdatedBy,
        id: "lastUpdatedBy",
        header: fetchClientAccResListLabel(
          "ClientAccountResourcesList.LastUpdatedBy"
        ),
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccResListLabel(
            "ClientAccountResourcesList.PlaceholderLastUpdatedBy"
          ),
        },
      },
      {
        accessorFn: getLastUpdatedOn,
        id: "lastUpdatedOn",
        header: fetchClientAccResListLabel(
          "ClientAccountResourcesList.LastUpdatedOn"
        ),
        Cell: EmptyCell,
        filterFn: "contains",
        sortingFn: "datetime",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientAccResListLabel(
            "ClientAccountResourcesList.PlaceholderLastUpdatedOn"
          ),
        },
      },
    ],
    [resourcesCMSData, canViewNewFields]
  );

  useEffect(() => {
    if (isSuccessOnDelete) {
      const isDeleteMode = !!variablesOnDelete?.isDeleteMode;
      const msg = isDeleteMode
        ? fetchMngResoRepoListLabel(
            "ManageResourceRepoList.DeletionCompletedMsg"
          )
        : fetchMngResoRepoListLabel(
            "ManageResourceRepoList.RecoveryCompleteMessage"
          );
      setSuccessMsg(msg);
      setOpenSuccessDialog(true);
    }
  }, [isSuccessOnDelete, variablesOnDelete]);

  const navigate = useNavigate();
  const [openMpgConfirmDialog, setOpenMpgConfirmDialog] = useState(false);
  const [idTobeDeletedOrRecovered, setIdTobeDeletedOrRecovered] = useState("");

  const { trackActivity } = useTrackUserActivity();

  useEffect(() => {
    if (isSuccessOnDelete) {
      trackActivity({
        activitySuccess: isSuccessOnDelete,
        actionType: variablesOnDelete?.isDeleteMode ? "Deleted" : "Recovered",
        itemId: variablesOnDelete?.programDescriptorId || "",
      });
    }
  }, [variablesOnDelete, isSuccessOnDelete]);

  return (
    <Fragment>
      {!!isLoadingOnPDData && fetchCommonCMSLabel("Common.Waitloading")}

      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {fetchMngResoRepoListLabel("ManageResourceRepoList.ResourceRepoTitle")}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        m={1}
      >
        <ButtonGroup>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              component={Link}
              to={managePDCreatePath}
              disabled={!!hasA4Access}
            >
              {fetchMngResoRepoListLabel(
                "ManageResourceRepoList.UploadNewResources"
              )}
            </Button>
            <Button
              variant={isRecycleRoute ? "contained" : "outlined"}
              size="small"
              component={Link}
              to={managePDRecyclePath}
            >
              {fetchMngResoRepoListLabel("ManageResourceRepoList.RecycleBin") ||
                "Recycle Bin"}
            </Button>
          </Box>
        </ButtonGroup>
      </Stack>

      {!!isSuccess && !tableData.length && <NoTableDataComp />}

      {!!isSuccess && !!tableData.length && (
        <MaterialReactTable
          columns={columns}
          data={tableData}
          layoutMode="grid"
          enableGlobalFilter={false}
          enableDensityToggle={false}
          initialState={{ density: "compact", showColumnFilters: true }}
          state={{
            isLoading: isLoadingOnPDData,
            columnPinning: {
              left: ["mrt-row-actions", "programDescripitorTitle"],
            },
          }}
          displayColumnDefOptions={{
            "mrt-row-actions": {
              header: fetchClientAccResDtlsLabel(
                "ClientAccountResourcesDetails.Actions"
              ),
              size: 100,
            },
          }}
          enableStickyHeader
          enableRowActions
          enablePinning
          renderToolbarInternalActions={({ table }) => (
            <>
              {canExportXls && !isRecycleRoute && (
                <ExportButton
                  table={table}
                  tooltipTitle={fetchClientAccResDtlsLabel(
                    "ClientAccountResourcesDetails.ExportToXLS"
                  )}
                  trackActivity={trackActivityMutate}
                  userId={userId}
                  exportColumns={exportColumns}
                  fileName={exportFileName}
                  worksheetName={exportWorksheetName}
                />
              )}
              <MRTToggleFiltersButton table={table} />
              <MRTShowHideColumnsButton table={table} />
              <MRTFullScreenToggleButton table={table} />
            </>
          )}
          muiTablePaginationProps={{
            labelRowsPerPage: fetchCommonCMSLabel("Common.Rowsperpage"),
          }}
          renderRowActions={({ row }) => {
            const Id = row.original?.Id;
            const IsActive = row.original?.IsActive;
            const handleDeleteClick = () => {
              setIdTobeDeletedOrRecovered({ id: Id, isActive: IsActive });
              setOpenMpgConfirmDialog(true);
            };
            return (
              <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
                {!!IsActive && (
                  <IconButton
                    onClick={() => navigate(managePDEditPath({ id: Id }))}
                  >
                    <EditIcon />
                  </IconButton>
                )}
                <IconButton
                  onClick={handleDeleteClick}
                  disabled={!!hasA4Access}
                >
                  {IsActive ? (
                    <Tooltip
                      title={fetchMngResoRepoListLabel(
                        "ManageResourceRepoList.ToolTipDeactivate"
                      )}
                    >
                      <DeleteIcon />
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title={fetchMngResoRepoListLabel(
                        "ManageResourceRepoList.ToolTipRecover"
                      )}
                    >
                      <ReplayIcon />
                    </Tooltip>
                  )}
                </IconButton>
              </Box>
            );
          }}
        />
      )}

      <MpgRedefinedConfirmDialog
        title={fetchClientAccResDtlsLabel(
          "ClientAccountResourcesDetails.ConfirmYourAction"
        )}
        content={`${
          idTobeDeletedOrRecovered?.isActive
            ? fetchMngResoRepoListLabel(
                "ManageResourceRepoList.ConfirmDeactivation"
              )
            : fetchMngResoRepoListLabel("ManageResourceRepoList.ConfirmRecover")
        }`}
        open={openMpgConfirmDialog}
        setOpen={setOpenMpgConfirmDialog}
        yesLabel={fetchCommonCMSLabel("Common.Yes")}
        noLabel={fetchCommonCMSLabel("Common.No")}
        extraLogicHandlerOnAgree={async () => {
          const id = idTobeDeletedOrRecovered?.id;
          const isActive = idTobeDeletedOrRecovered?.isActive;
          const result = await checkAssociationsAsync({ reportResourceId: id });
          const clients = result?.data || [];
          if (clients.length > 0 && isActive) {
            const baseMsg = fetchMngResoRepoListLabel(
              "ManageResourceRepoList.ErrorMsgResourceAssociatedActiveClientPortal"
            );
            const listLines = clients.map(
              (c, idx) =>
                `${idx + 1}. ${
                  c.ClientAccountName || c.ClientName || c.SiteName || "Client"
                }`
            );
            setAssociationError([baseMsg, "", ...listLines].join("\n"));
            setOpenAssociationDialog(true);
          } else {
            deletePDById({ isDeleteMode: !!isActive, programDescriptorId: id });
          }
          setIdTobeDeletedOrRecovered("");
        }}
      />

      <MpgInfoDialog
        open={openAssociationDialog}
        setOpen={setOpenAssociationDialog}
        title="Cannot Deactivate"
        content={associationError}
      />

      <MpgInfoDialog
        open={openSuccessDialog}
        setOpen={setOpenSuccessDialog}
        title=""
        content={successMsg}
      />

      <MpgInfoDialog
        open={!!isError}
        setOpen={(open) => {
          if (!open) resetPDList();
        }}
        content={error?.message}
      />
    </Fragment>
  );
};

export default ViewClientAccPD;
